import { ChangeDetectorRef, Component, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, Location as AngularLocation } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ItemSelectorComponent } from 'src/app/shared/components/item-selector/item-selector.component';
import { BasePage } from 'src/app/shared/base-classes/base-page';
import { Item, ShopCategory } from 'src/app/shared/models/items.model';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ActivatedRoute, Router } from '@angular/router';
import { JsonReaderService } from 'src/app/services/json-reader/json-reader.service';
import { AoDataGetterService } from 'src/app/services/ao-data-getter/ao-data-getter.service';
import { AlbionImageService } from 'src/app/services/albion-image/albion-image.service';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { PRICE_TYPES } from 'src/app/shared/constants/market.constant';
import { MarketHistoriesResponse, MarketResponse } from 'src/app/shared/models/ao-data-interfaces';
import { ItemTradeCardComponent } from '../crafting/craft-result/item-trade-card/item-trade-card.component';
import { SparkLineChartComponent } from 'src/app/shared/components/spark-line-chart/spark-line-chart.component';
import { Location } from 'src/app/shared/models/location.model';
import { MatDividerModule } from '@angular/material/divider';
import { UserSettingsService } from 'src/app/services/user-settings/user-settings.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { DialogComponent } from 'src/app/shared/components/dialog/dialog.component';

@Component({
  selector: 'app-transport',
  imports: [
    CommonModule,
    MatInputModule,
    FormsModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatTabsModule,
    MatFormFieldModule,
    ItemSelectorComponent,
    MatCheckboxModule,
    ItemTradeCardComponent,
    SparkLineChartComponent,
    MatDividerModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './transport.component.html',
  styleUrls: ['./transport.component.scss'],
})
export class TransportComponent extends BasePage {
  prices: MarketResponse[] | null = null;
  histories: MarketHistoriesResponse[] | null = null;
  realBuyPrice: number = 0;
  realSellPriceMap: Map<Location, number> = new Map<Location, number>();
  disableRefresh: boolean = false;

  disableSaveConfig: boolean = false;
  constructor(
    route: ActivatedRoute,
    router: Router,
    urlLocation: AngularLocation,
    jsonReaderService: JsonReaderService,
    aoDataGetter: AoDataGetterService,
    albionImageService: AlbionImageService,
    userSettingsService: UserSettingsService,
    @Inject(PLATFORM_ID) platformId: Object,
    private snackBar: MatSnackBar,
    private matDialog: MatDialog,
    cdr: ChangeDetectorRef
  ) {
    super(route, router, urlLocation, jsonReaderService, aoDataGetter, albionImageService, userSettingsService, cdr, platformId);
  }

  setRealSellPrice(location: Location, value: number): void {
    this.realSellPriceMap.set(location, value);
  }

  getAverage(location: Location): {
    averagePrice: number | undefined;
    averageAmount: number | undefined;
  } {
    if (this.selectedItem && this.histories)
      return this.aoDataGetter.getAveragePriceAndAmount(
        this.selectedItem,
        location,
        this.histories,
        undefined,
        this.userSettingsService.getUserTransportConfig().averageNumberOfDays
      );
    else return { averagePrice: 0, averageAmount: 0 };
  }

  get itemBuyPrice(): { value: number; age: number } {
    if (this.prices) {
      if (this.userSettingsService.getUserTransportConfig().buyPriceType === PRICE_TYPES.buy) {
        let price = this.prices?.find((x) => x.item === this.selectedItem && x.city === this.userSettingsService.getUserTransportConfig().buyLocation);
        if (price) {
          return { value: price.buyPriceMax, age: this.getHoursDifference(price?.buyPriceMaxDate) };
        }
      }
      if (this.userSettingsService.getUserTransportConfig().buyPriceType === PRICE_TYPES.sell) {
        let price = this.prices?.find((x) => x.item === this.selectedItem && x.city === this.userSettingsService.getUserTransportConfig().buyLocation);
        if (price) {
          return { value: price.sellPriceMin, age: this.getHoursDifference(price?.sellPriceMinDate) };
        }
      }
      if (this.userSettingsService.getUserTransportConfig().buyPriceType === PRICE_TYPES.average) {
        if (this.selectedItem && this.histories) {
          return { value: Math.round(this.getAverage(this.userSettingsService.getUserTransportConfig().buyLocation).averagePrice ?? 0), age: 0 };
        }
      }
    }
    return { value: 0, age: 0 };
  }
  getItemSellPrice(location: Location): { value: number; age: number } {
    if (this.prices) {
      if (this.userSettingsService.getUserTransportConfig().sellPriceType === PRICE_TYPES.buy) {
        let price = this.prices?.find((x) => x.item === this.selectedItem && x.city === location);
        if (price) {
          return { value: price.buyPriceMax, age: this.getHoursDifference(price?.buyPriceMaxDate) };
        }
      }
      if (this.userSettingsService.getUserTransportConfig().sellPriceType === PRICE_TYPES.sell) {
        let price = this.prices?.find((x) => x.item === this.selectedItem && x.city === location);
        if (price) {
          return { value: price.sellPriceMin, age: this.getHoursDifference(price?.sellPriceMinDate) };
        }
      }
      if (this.userSettingsService.getUserTransportConfig().sellPriceType === PRICE_TYPES.average) {
        if (this.selectedItem && this.histories) {
          return { value: Math.round(this.getAverage(location).averagePrice ?? 0), age: 0 };
        }
      }
    }
    return { value: 0, age: 0 };
  }
  get totalBuyPrice(): number {
    return this.realBuyPrice * this.userSettingsService.getUserTransportConfig().amount;
  }
  getTotalSellPrice(location: Location): number {
    return (this.realSellPriceMap.get(location) ?? 0) * this.userSettingsService.getUserTransportConfig().amount;
  }
  get buySetupFee(): number {
    if (
      this.userSettingsService.getUserTransportConfig().buyPriceType === PRICE_TYPES.average ||
      this.userSettingsService.getUserTransportConfig().buyPriceType === PRICE_TYPES.buy
    ) {
      return this.totalBuyPrice * this.userSettingsService.getUserTransportConfig().setupFee.value;
    } else return 0;
  }
  getSellSetupFee(location: Location): number {
    if (
      this.userSettingsService.getUserTransportConfig().sellPriceType === PRICE_TYPES.average ||
      this.userSettingsService.getUserTransportConfig().sellPriceType === PRICE_TYPES.sell
    ) {
      return this.getTotalSellPrice(location) * this.userSettingsService.getUserTransportConfig().setupFee.value;
    } else return 0;
  }
  getSellSalesTaxes(location: Location): number {
    return this.getTotalSellPrice(location) * this.userSettingsService.getUserTransportConfig().salesTax.value;
  }
  getResult(location: Location): number {
    return this.getTotalSellPrice(location) - this.totalBuyPrice - this.buySetupFee - this.getSellSetupFee(location) - this.getSellSalesTaxes(location);
  }
  getResultPercentage(location: Location): number {
    return this.getResult(location) / (this.totalBuyPrice + this.buySetupFee);
  }
  get totalWeight(): number {
    return (this.selectedItem?.weight ?? 0) * this.userSettingsService.getUserTransportConfig().amount;
  }

  override ngOnInit() {
    super.ngOnInit();
    this.prices$.subscribe((prices) => {
      this.prices = prices;
      this.updateRealPrices();
    });
    this.histories$.subscribe((histories) => {
      this.histories = histories;
      this.updateRealPrices();
    });
  }

  filterItemsForSelect(items: Item[]): Item[] {
    return items;
  }

  filterShopCategoriesForSelect(categories: ShopCategory[]): ShopCategory[] {
    return categories;
  }

  filterItemsForPrices(items: Item[]): Item[] {
    if (items && this.selectedItem) {
      return items.filter((item) => item.originalUniqueName === this.selectedItem?.originalUniqueName);
    } else {
      return [];
    }
  }

  getHoursDifference(priceDate: Date): number {
    const differenceInMilliseconds = new Date().getTime() - priceDate.getTime();
    const differenceInHours = differenceInMilliseconds / (1000 * 60 * 60);
    return differenceInHours;
  }

  updateRealPrices(): void {
    this.updateRealBuyPrice();
    this.updateRealSellPrice();
  }

  updateRealBuyPrice(): void {
    this.realBuyPrice = Math.round(this.itemBuyPrice.value);
  }
  updateRealSellPrice(): void {
    this.realSellPriceMap.clear();
    for (let location of this.locations) {
      this.realSellPriceMap.set(location, this.getItemSellPrice(location).value);
    }
  }
  async saveTransportConfiguration(): Promise<void> {
    if (this.userSettingsService.isLoggedIn) {
      this.disableSaveConfig = true;

      await this.userSettingsService.updateUserTransportConfig(this.userSettingsService.getUserTransportConfig());

      setTimeout(() => {
        this.disableSaveConfig = false;
      }, 5000);
    } else {
      this.snackBar.open(`You must be Logged In to save!`, undefined, { duration: 2000 });
    }
  }
  refreshPrices(): void {
    const dialogRef = this.matDialog.open(DialogComponent, {
      width: '250px',
      data: { title: 'Confirm Refresh Prices', message: `Are you sure you want to refresh the prices? All the prices you typed manually will be lost!` },
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        this.disableRefresh = true;
        this.updatePrices(this.items ?? []);
        this.updateHistories(this.items ?? []);
        setTimeout(() => {
          this.disableRefresh = false;
        }, 5000);
      }
    });
  }
}
